import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Loader2, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { registerUser, loginUser } from "@/lib/api";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal({ trigger, defaultMode = "login", onGoogle, onDemo, googleLoading = false, demoLoading = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(defaultMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const inputId = useId();

  const isRegister = mode === "register";
  const pending = busy || googleLoading || demoLoading;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isRegister) {
        const result = await registerUser(email.trim(), password, name.trim() || undefined);
        if (result.confirmation_required) {
          setConfirmationEmail(email.trim());
          setPassword("");
          toast.success("Check your email to confirm your account, then log in.");
          setMode("login");
          return;
        }
        toast.success("Account created — welcome to SEO Signals");
      } else {
        await loginUser(email.trim(), password);
        toast.success("Welcome back");
      }
      setOpen(false);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setError(""); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto bg-white p-0 gap-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle className="font-heading font-bold text-xl text-navy tracking-tight">
            {isRegister ? "Create your account" : "Welcome back"}
          </DialogTitle>
          <DialogDescription className="text-sm text-navy/60 mt-1">
            {isRegister
              ? "Create an account with email or continue with Google."
              : "Continue with Google or log in with your email."}
          </DialogDescription>
        </DialogHeader>

        {onGoogle && <p className="text-sm text-navy/70">Google shares your basic profile and email address so we can create and secure your SEO Signals account. This step does not connect Search Console or Google Analytics. <a href="/privacy">Privacy policy</a> · <a href="/terms">Terms</a>.</p>}
        {onGoogle && (
          <div className="px-6 pt-5">
            <button type="button" onClick={onGoogle} disabled={pending}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-5 py-3 font-semibold text-navy hover:bg-slate-50 disabled:opacity-60">
              {googleLoading && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
              Sign in with Google
            </button>
            <p className="mt-4 text-center text-sm text-navy/60">Or continue with email</p>
          </div>
        )}
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {confirmationEmail && <p data-testid="auth-confirmation" role="status" className="text-sm text-navy bg-slate-50 rounded-md px-3 py-2">Check your email at {confirmationEmail} and follow the link to confirm your account, then log in here. If it hasn't arrived, check your spam folder or <a href="/contact" className="font-semibold underline">contact support</a>.</p>}
          {isRegister && (
            <div>
              <label htmlFor={`${inputId}-name`} className="text-xs font-semibold uppercase tracking-wider text-navy/50">Name</label>
              <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 focus-within:ring-2 focus-within:ring-emerald/25">
                <User size={16} className="text-navy/40" />
                <input
                  id={`${inputId}-name`} name="name" autoComplete="name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Your name" data-testid="auth-name"
                  className="flex-1 bg-transparent text-sm text-navy placeholder:text-navy/35 focus:outline-none"
                />
              </div>
            </div>
          )}
          <div>
            <label htmlFor={`${inputId}-email`} className="text-xs font-semibold uppercase tracking-wider text-navy/50">Email</label>
            <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 focus-within:ring-2 focus-within:ring-emerald/25">
              <Mail size={16} className="text-navy/40" />
              <input
                id={`${inputId}-email`} name="email" autoComplete="email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yoursite.com" data-testid="auth-email"
                className="flex-1 bg-transparent text-sm text-navy placeholder:text-navy/35 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label htmlFor={`${inputId}-password`} className="text-xs font-semibold uppercase tracking-wider text-navy/50">Password</label>
            <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 focus-within:ring-2 focus-within:ring-emerald/25">
              <Lock size={16} className="text-navy/40" />
              <input
                id={`${inputId}-password`} name="password" autoComplete={isRegister ? "new-password" : "current-password"}
                type="password" required minLength={isRegister ? 8 : undefined}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegister ? "At least 8 characters" : "Your password"} data-testid="auth-password"
                className="flex-1 bg-transparent text-sm text-navy placeholder:text-navy/35 focus:outline-none"
              />
            </div>
          </div>

          {error && <p data-testid="auth-error" className="text-sm text-amber-dark bg-amber/10 rounded-md px-3 py-2">{error}</p>}

          <button
            type="submit" disabled={pending} data-testid="auth-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-emerald px-5 py-3 text-base font-semibold text-white hover:bg-emerald-dark transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
            {isRegister ? "Create account" : "Log in"}
          </button>

          <p className="text-center text-sm text-navy/55">
            {isRegister ? "Already have an account?" : "New to SEO Signals?"}{" "}
            <button
              type="button" data-testid="auth-toggle-mode"
              onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }}
              className="font-semibold text-emerald-dark hover:underline"
            >
              {isRegister ? "Log in" : "Create a free account"}
            </button>
          </p>
        </form>
        {onDemo && (
          <div className="border-t border-slate-200 bg-slate-50 px-6 py-5 text-center">
            <button type="button" onClick={onDemo} disabled={pending} data-testid="auth-demo"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-emerald px-4 py-3 text-sm font-semibold text-emerald-dark hover:bg-emerald/10 disabled:opacity-60">
              {demoLoading && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
              {demoLoading ? "Opening demo…" : "Try the demo—no account needed"}
            </button>
            <p className="mt-2 text-xs text-navy/60">Explore with sample data. No Google connection required.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
